import React from 'react';
import { isPlayable, playbackUrlFor } from '../../../../services/studentTracksModel';
import './VocalsControl.css';

/**
 * Vocals in / out, and how loud (Rehearsal mode, 2026-09-28). Anthony:
 * "dropping the vocals in and out with a button click, also maybe a volume
 * slider on the vocals. Sometimes they like to have the vocals on a lower
 * level."
 *
 * It needs the job's kept Vocals stem (filed since 2026-09-28) beside the
 * Track: the Track is the band, the Vocals file is the singer, and the player
 * holds the two to one clock. So it shows for the tab's own Track or
 * Reference when a Vocals file from the SAME job exists, and never for a
 * rendered saved key (that file is already in another key; the live key
 * change moves both halves together instead).
 *
 * On the Reference (vocals baked in) the control offers the switch to the
 * Track at the same moment, since a baked-in vocal cannot be turned down.
 * Always rendered where it applies; the slider stays visible while the
 * vocals are out, muted by colour, so it is findable (keratoconus; no hover).
 */
export const vocalsPartnerOf = (file, files = []) => {
	if (!file || file.key || !file.jobId) return null;
	const v = String(file.variant || '').toLowerCase();
	if (v !== 'track' && v !== 'reference') return null;
	return (files || []).find((f) => f && !f.superseded && f.jobId === file.jobId && String(f.variant || '').toLowerCase() === 'vocals' && isPlayable(f) && playbackUrlFor(f)) || null;
};

const trackOf = (file, files = []) =>
	(files || []).find((f) => f && !f.superseded && !f.key && f.jobId === file.jobId && String(f.variant || '').toLowerCase() === 'track' && playbackUrlFor(f)) || null;

const VocalsControl = ({ file, files = [], player }) => {
	const partner = vocalsPartnerOf(file, files);
	if (!partner || !player || typeof player.setVocals !== 'function') return null;
	const isReference = String(file.variant || '').toLowerCase() === 'reference';
	const track = isReference ? trackOf(file, files) : file;
	if (!track) return null;

	const state = player.vocals && player.vocals.file && player.vocals.file.storagePath === partner.storagePath ? player.vocals : null;
	const on = Boolean(state && state.on);
	const level = state ? state.level : 1;
	const pct = Math.round(level * 100);

	const setOn = (next) => {
		if (isReference) player.swapTo(track);
		player.setVocals({ file: partner, on: next, level });
	};
	const setLevel = (n) => {
		if (isReference) player.swapTo(track);
		player.setVocals({ file: partner, on: true, level: n });
	};

	return (
		<div className="ld-vocals" role="group" aria-label="Vocals">
			<span className="ld-vocals__label">Vocals</span>
			{isReference ? (
				<button type="button" className="ld-vocals__btn" onClick={() => setOn(true)}>
					<i className="fa-sharp fa-solid fa-sliders" aria-hidden="true" />
					Adjust the vocals
				</button>
			) : (
				<div className="ld-vocals__seg">
					<button type="button" className={`ld-vocals__btn${!on ? ' is-on' : ''}`} aria-pressed={!on} onClick={() => setOn(false)}>
						Out
					</button>
					<button type="button" className={`ld-vocals__btn${on ? ' is-on' : ''}`} aria-pressed={on} onClick={() => setOn(true)}>
						In
					</button>
				</div>
			)}
			{!isReference && (
				<label className={`ld-vocals__level${on ? '' : ' is-idle'}`}>
					<span>Level</span>
					<input type="range" min="0" max="100" step="5" value={pct} aria-label="Vocal level" onChange={(e) => setLevel(Number(e.target.value) / 100)} />
					<span className="ld-vocals__pct">{pct}%</span>
				</label>
			)}
			{isReference && <p className="ld-vocals__note">Switches to the Track at this moment, with the singer on a level you can set.</p>}
		</div>
	);
};

export default VocalsControl;
